import React from 'react';
import { Modal, View, Text, Pressable, Platform, ActivityIndicator, TextInput, FlatList, Keyboard, TouchableWithoutFeedback, LayoutAnimation, UIManager } from 'react-native';
import * as Haptics from 'expo-haptics';
import { MaterialIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ReanimatedSwipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import { CameraView, useCameraPermissions, BarcodeScanningResult, scanFromURLAsync } from 'expo-camera';
import { extractSerialCandidates } from './serialRegex';
// @ts-ignore - Platform-specific file (.ios/.android/.web)
import OcrService from './OcrService';
import * as ImageManipulator from 'expo-image-manipulator';
import { addChargerToPartner, AddPartnerChargerResponse_t, removePartnerCharger } from '../../services/partner-service';
import { isSuccessful } from '../../services/api-requests';
import { notificationService } from '../../services/notification-service';
import * as Clipboard from 'expo-clipboard';

type Props = {
    visible: boolean;
    onClose: () => void;
    onSerialDetected: (serial: string) => void;
    manufacturer_name: string;
    onAddedSuccess?: (info: { manufacturer: string; serial: string; chargerId?: string; associationId?: string }) => void;
    singleMode?: boolean;
};

const SerialScanModal: React.FC<Props> = ({ visible, onClose, onSerialDetected, manufacturer_name, onAddedSuccess, singleMode = false }) => {
    const cameraRef = React.useRef<any>(null);
    const insets = useSafeAreaInsets();
    const [hasPermission, setHasPermission] = React.useState<boolean | null>(null);
    const [isScanning, setIsScanning] = React.useState(false);
    const scanTokenRef = React.useRef(0);
    const [torch, setTorch] = React.useState(false);
    const [_status, requestPermission] = useCameraPermissions();
    
    
    const [manualSerial, setManualSerial] = React.useState<string>('');
    const [items, setItems] = React.useState<Array<{ id: string; serial: string; status: 'pending' | 'valid' | 'dupe' | 'error'; reason?: string; chargerId?: string; associationId?: string }>>([]);
    const processed = React.useRef<Set<string>>(new Set());
    const lastPopupSerial = React.useRef<string | null>(null);
    const [successOverlay, setSuccessOverlay] = React.useState<{ open: boolean; manufacturer: string; serial: string; chargerId?: string } | null>(null);
    const [copiedId, setCopiedId] = React.useState<string | null>(null);

    // Enable layout animations on Android
    React.useEffect(() => {
        if (Platform.OS === 'android' && (UIManager as any).setLayoutAnimationEnabledExperimental) {
            (UIManager as any).setLayoutAnimationEnabledExperimental(true);
        }
    }, []);

    const animate = () => LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);

    const copySerial = async (item: { id: string; serial: string }) => {
        try {
            await Clipboard.setStringAsync(item.serial);
            setCopiedId(item.id);
            // notificationService.sendNotification('success', 'Copied');
            setTimeout(() => setCopiedId(null), 1200);
        } catch {
            // notificationService.sendNotification('error', 'Unable to copy');
        }
    };

    const removeItem = (id: string) => {
        setItems(prev => prev.filter(i => i.id !== id));
        const removed = items.find(i => i.id === id);
        if (removed) processed.current.delete(removed.serial);
    };

    React.useEffect(() => {
        (async () => {
            if (Platform.OS === 'web') {
                setHasPermission(false);
                return;
            }
            const { status } = await requestPermission();
            setHasPermission(status === 'granted');
        })();
    }, []);

    const withTimeout = async <T,>(p: Promise<T>, ms: number, label: string): Promise<T> => {
        return await Promise.race([
            p,
            new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`timeout:${label}`)), ms)) as any,
        ]) as T;
    };

    const cancelScan = () => {
        const prev = scanTokenRef.current;
        scanTokenRef.current = prev + 1;
        // console.log('[SerialScanModal] cancelScan token', { prev, next: scanTokenRef.current });
        setIsScanning(false);
    };

    const handleScan = async () => {
        if (!cameraRef.current || isScanning) return;
        try {
            const token = ++scanTokenRef.current;
            setIsScanning(true);
            // console.log('[SerialScanModal] handleScan start', { token });
            const photo = await cameraRef.current.takePictureAsync({ 
                skipProcessing: true, 
                quality: 1, 
                base64: true, 
                imageType: 'png', 
                exif: true });
            if (scanTokenRef.current !== token) return;
            if (!photo?.uri) return;
            const imgW = photo.width ?? 0;
            const imgH = photo.height ?? 0;
            if (!imgW || !imgH) return;
            const overlayTopPct = 0.30;
            const overlayLeftPct = 0.10;
            const overlayRightPct = 0.10;
            const overlayHeightPct = 0.28;
            const cropX = Math.round(imgW * overlayLeftPct);
            const cropY = Math.round(imgH * overlayTopPct);
            const cropW = Math.round(imgW * (1 - overlayLeftPct - overlayRightPct));
            const cropH = Math.round(imgH * overlayHeightPct);
            const cropped = await withTimeout(ImageManipulator.manipulateAsync(
                photo.uri,
                [
                    { crop: { originX: cropX, originY: cropY, width: cropW, height: cropH } },
                    { resize: { width: 1400 } },
                ],
                { compress: 1, format: ImageManipulator.SaveFormat.PNG, base64: true }
            ), 8000, 'imageManipulate');
            if (scanTokenRef.current !== token) return;
            // console.log('[SerialScanModal] cropped', { cropX, cropY, cropW, cropH, uri: cropped?.uri });
            let ocr = (await withTimeout(OcrService.recognizeFromBase64(cropped.base64), 8000, 'ocrBase64')) || (await withTimeout(OcrService.recognizeFromImage(cropped.uri), 8000, 'ocrImage')) as any;
            // console.log('[SerialScanModal] ocr(crop) length', (ocr?.text || '').length);
            let text = ocr.text || '';
            if (!text || text.trim().length < 6) {
                // Fallback: run OCR on the full image if crop produced little/no text
                const full = await withTimeout(ImageManipulator.manipulateAsync(
                    photo.uri,
                    [{ resize: { width: 1600 } }],
                    { compress: 1, format: ImageManipulator.SaveFormat.PNG, base64: true }
                ), 8000, 'imageResizeFull');
                if (scanTokenRef.current !== token) return;
                const ocrFull = (await withTimeout(OcrService.recognizeFromBase64(full.base64), 8000, 'ocrBase64Full')) || (await withTimeout(OcrService.recognizeFromImage(full.uri), 8000, 'ocrImageFull')) as any;
                // console.log('[SerialScanModal] ocr(full) length', (ocrFull?.text || '').length);
                if ((ocrFull.text || '').length > (text.length || 0)) {
                    ocr = ocrFull;
                    text = ocrFull.text || '';
                }
            }
            // console.log('[SerialScanModal] text preview', (text || '').slice(0, 180));
            const candidates = extractSerialCandidates(manufacturer_name, text);
            // console.log('[SerialScanModal] candidates', candidates);
            // If OCR already yielded a plausible serial, stop spinner and validate in background immediately
            if (candidates.length > 0) {
                // try { notificationService.sendNotification('success', `OCR candidates: ${candidates.length}`); } catch {}
                setIsScanning(false);
                setTimeout(() => { void handleDetectedSerial(candidates[0]); }, 0);
                return;
            }

            try {
                const types = ['ean13','ean8','upc_a','upc_e','code128','code39','pdf417','aztec','datamatrix'] as any;
                const tryScan = async (uri: string) => withTimeout(scanFromURLAsync(uri, { barcodeTypes: types } as any), 2000, 'barcodeScan');
                let codes = await tryScan(cropped.uri);
                if (scanTokenRef.current !== token) return;
                if (!(codes && Array.isArray(codes) && codes.length > 0)) {
                    for (const angle of [90, 180, 270]) {
                        const rotated = await withTimeout(ImageManipulator.manipulateAsync(
                            cropped.uri,
                            [{ rotate: angle }],
                            { compress: 1, format: ImageManipulator.SaveFormat.PNG }
                        ), 2000, 'rotate');
                        if (scanTokenRef.current !== token) return;
                        codes = await tryScan(rotated.uri);
                        if (scanTokenRef.current !== token) return;
                        if (codes && Array.isArray(codes) && codes.length > 0) break;
                    }
                }
                // console.log('[SerialScanModal] barcode matches', Array.isArray(codes) ? codes.length : 0);
                if (codes && Array.isArray(codes) && codes.length > 0) {
                    const data = codes[0]?.data?.toString?.() ?? '';
                    if (data) {
                        // stop spinner immediately, process in background
                        setIsScanning(false);
                        setTimeout(() => { void handleDetectedSerial(data); }, 0);
                        return;
                    }
                }
            } catch (_) {
                // ignore
            }
            // No OCR candidates and no barcode matches
        } catch (e: any) {
            console.log('[SerialScanModal] handleScan error', e?.message || e);
        } finally {
            setIsScanning(false);
        }
    };

    const handleBarcodeScan = (result: BarcodeScanningResult) => {
        if (!result || isScanning) return;
        const data = result.data?.toString?.() ?? '';
        if (!data) return;
        // try { notificationService.sendNotification('success', `Barcode: ${data.slice(0, 20)}`); } catch {}
        void handleDetectedSerial(data);
    };

    const handleDetectedSerial = async (raw: string) => {
        const candidates = extractSerialCandidates(manufacturer_name, raw);
        const serial = (candidates[0] || raw).toUpperCase();
        if (!serial || serial.replace(/[^A-Z0-9\-]/g, '').length < 6) return;
        
        // Single mode: just return the serial and close
        if (singleMode) {
            onSerialDetected(serial);
            onClose();
            return;
        }
        
        if (processed.current.has(serial)) return;
        processed.current.add(serial);
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        animate();
        setItems(prev => [{ id, serial, status: 'pending' }, ...prev]);
        try {
            const resp = await addChargerToPartner({ manufacturer_name, unit_serial: serial });
            const status = resp.status;
            const payload = resp.payload as AddPartnerChargerResponse_t | null;
            // console.log('[SerialScanModal] addChargerToPartner status=', status, 'payload=', payload);
            const succeeded = isSuccessful(status);
            const chargerId = succeeded ? (payload?.chargerId || undefined) : undefined;
            const associationId = succeeded ? (payload?.associationId || undefined) : undefined;
            animate();
            setItems(prev => prev.map(it => it.id === id ? {
                ...it,
                status: succeeded ? 'valid' : (status === 409 ? 'dupe' : 'error'),
                reason: !succeeded && payload ? payload.message : it.reason,
                chargerId,
                associationId,
            } : it));
            if (succeeded) {
                setSuccessOverlay({ open: true, manufacturer: manufacturer_name, serial, chargerId });
                if (typeof onAddedSuccess === 'function') {
                    // console.log('[SerialScanModal] triggering onAddedSuccess', { manufacturer: manufacturer_name, serial, chargerId, associationId });
                    onAddedSuccess({ manufacturer: manufacturer_name, serial, chargerId, associationId });
                }
            }
        } catch (e: any) {
            console.log('[SerialScanModal] addChargerToPartner error', e);
            setItems(prev => prev.map(it => it.id === id ? { ...it, status: 'error', reason: e?.message || 'Network error' } : it));
        }
    };

    // Counters
    const okCount = items.filter(i => i.status === 'valid').length;
    const dupeCount = items.filter(i => i.status === 'dupe').length;
    const errCount = items.filter(i => i.status === 'error').length;

    const renderRightActions = (item: { id: string; status: 'pending' | 'valid' | 'dupe' | 'error'; chargerId?: string }) => (
        <View style={{ justifyContent: 'center', alignItems: 'center', backgroundColor: '#8E2C2C', width: 80, height: '100%' }}>
            <Pressable onPress={async () => {
                if (item.status === 'valid' && item.chargerId) {
                    try { await removePartnerCharger({ chargerId: item.chargerId }); } catch {}
                }
                removeItem(item.id);
            }} style={{ padding: 12 }}>
                <MaterialIcons name="delete" size={22} color="#FFFFFF" />
            </Pressable>
        </View>
    );

    const renderItem = ({ item }: { item: { id: string; serial: string; status: 'pending' | 'valid' | 'dupe' | 'error'; reason?: string } }) => {
        const content = (
            <Pressable
                onPress={() => copySerial(item)}
                style={{ paddingVertical: 10, paddingHorizontal: 12, borderBottomColor: '#2A2E33', borderBottomWidth: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'transparent' }}
            >
                <View style={{ flex: 1, paddingRight: 8 }}>
                    <Text numberOfLines={1} style={{ color: '#FFFFFF' }}>{item.serial}</Text>
                    <Text numberOfLines={1} style={{ color: '#8D8E90', fontSize: 12 }}>
                        {item.status === 'pending' ? 'Validating…' : item.status === 'valid' ? 'Added' : item.status === 'dupe' ? 'Duplicate' : `Rejected${item.reason ? `: ${item.reason}` : ''}`}
                    </Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    {item.status === 'pending' && <ActivityIndicator color="#39BEB7" />}
                    <View style={{ paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999, backgroundColor: item.status === 'valid' ? '#2E7D32' : item.status === 'dupe' ? '#A1887F' : item.status === 'error' ? '#8E2C2C' : '#3A3F45' }}>
                        <Text style={{ color: 'white', fontSize: 12 }}>{item.status}</Text>
                    </View>
                    {copiedId === item.id && (
                        <View pointerEvents="none" style={{ marginLeft: 8, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999, backgroundColor: '#3A3F45', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <MaterialIcons name="check" size={14} color="#51B868" />
                            <Text style={{ color: '#FFFFFF', fontSize: 12 }}>Copied</Text>
                        </View>
                    )}
                </View>
            </Pressable>
        );
        if (item.status === 'error' || item.status === 'dupe' || item.status === 'valid') {
            return (
                <ReanimatedSwipeable renderRightActions={() => renderRightActions(item)}>
                    {content}
                </ReanimatedSwipeable>
            );
        }
        return content;
    };

    // Fallback: if a row turns valid but onAddedSuccess didn't fire (race), trigger it here once
    React.useEffect(() => {
        if (typeof onAddedSuccess !== 'function') return;
        const firstValid = items.find(i => i.status === 'valid' && i.serial !== lastPopupSerial.current);
        if (firstValid) {
            lastPopupSerial.current = firstValid.serial;
            onAddedSuccess({ manufacturer: manufacturer_name, serial: firstValid.serial, chargerId: (firstValid as any).chargerId, associationId: (firstValid as any).associationId });
        }
    }, [items]);

    return visible ? (
        <Modal visible animationType="slide" transparent>
            <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
            <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
                {/* Header with counters */}
                <View style={{ paddingHorizontal: 16, paddingVertical: 10, paddingTop: insets.top + 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={{ color: 'white', fontSize: 18, fontWeight: '600' }}>{singleMode ? 'Scan Serial' : 'Scan Serials'}</Text>
                    {!singleMode && (
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                            <View style={{ backgroundColor: '#2E7D32', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999 }}><Text style={{ color: 'white', fontSize: 12 }}>Added {okCount}</Text></View>
                            <View style={{ backgroundColor: '#6D4C41', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999 }}><Text style={{ color: 'white', fontSize: 12 }}>Dupes {dupeCount}</Text></View>
                            <View style={{ backgroundColor: '#8E2C2C', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999 }}><Text style={{ color: 'white', fontSize: 12 }}>Errors {errCount}</Text></View>
                        </View>
                    )}
                </View>

                {/* Camera strip */}
                <View style={{ height: singleMode ? '90%' : '55%' }}>
                    {hasPermission ? (
                        <View style={{ flex: 1 }}>
                            <CameraView
                                ref={r => { cameraRef.current = r; }}
                                style={{ flex: 1 }}
                                facing="back"
                                enableTorch={torch}
                                zoom={0.2}
                                onBarcodeScanned={isScanning ? undefined : handleBarcodeScan}
                                barcodeScannerSettings={{
                                    barcodeTypes: [
                                        'ean13', 'ean8', 'upc_a', 'upc_e', 'code128', 'code39', 'pdf417', 'aztec', 'datamatrix',
                                    ],
                                }}
                            />
                            <View style={{ position: 'absolute', top: 50, left: 20, right: 20, alignItems: 'center' }}>
                                <Text style={{ color: 'white', fontSize: 16 }}>Align the serial in the box, then Tap Scan</Text>
                            </View>
                            <View
                                style={{
                                    position: 'absolute',
                                    top: '30%',
                                    left: '10%',
                                    right: '10%',
                                    height: '28%',
                                    borderColor: '#39BEB7',
                                    borderWidth: 2,
                                    borderRadius: 8,
                                }}
                            />
                            
                            <View style={{ position: 'absolute', bottom: singleMode ? Math.max(insets.bottom + 20, 60) : 12, left: 16, right: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                {isScanning ? (
                                    <Pressable
                                        onPress={() => { try { Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {} cancelScan(); }}
                                        style={({ pressed }) => ({ padding: 10, backgroundColor: pressed ? '#2E3238' : '#23262B', borderRadius: 8 })}
                                    >
                                        <Text style={{ color: 'white' }}>Cancel</Text>
                                    </Pressable>
                                ) : (
                                    <Pressable
                                        onPress={async () => { try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {} setTorch((t) => !t); }}
                                        style={({ pressed }) => ({ padding: 10, backgroundColor: pressed ? '#2E3238' : '#23262B', borderRadius: 8 })}
                                    >
                                        <MaterialIcons name={torch ? 'flashlight-on' : 'flashlight-off'} size={20} color="#FFFFFF" />
                                    </Pressable>
                                )}
                                <Pressable
                                    onPress={async () => { try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {} await handleScan(); }}
                                    style={({ pressed }) => ({ padding: 12, backgroundColor: pressed ? '#2FB0A9' : '#39BEB7', borderRadius: 999, minWidth: 100, alignItems: 'center' })}
                                >
                                    {isScanning ? <ActivityIndicator color="#000" /> : <Text>Scan</Text>}
                                </Pressable>
                                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#23262B', borderRadius: 8, paddingHorizontal: 8, flex: 1 }}>
                                    <TextInput
                                        value={manualSerial}
                                        onChangeText={setManualSerial}
                                        placeholder="Enter/paste serial"
                                        placeholderTextColor="#8D8E90"
                                        autoCapitalize="characters"
                                        blurOnSubmit
                                        returnKeyType="done"
                                        onSubmitEditing={() => { const s = manualSerial.trim(); if (s) { void handleDetectedSerial(s); setManualSerial(''); } }}
                                        style={{ flex: 1, color: '#FFFFFF', paddingVertical: 6 }}
                                    />
                                    <Pressable
                                        onPress={async () => { const s = manualSerial.trim(); if (s) { try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {} void handleDetectedSerial(s); setManualSerial(''); } }}
                                        style={({ pressed }) => ({ paddingVertical: 8, paddingHorizontal: 12 })}
                                    >
                                        {({ pressed }) => (
                                            <MaterialIcons name="add" size={20} color={pressed ? '#51B868' : '#39BEB7'} />
                                        )}
                                    </Pressable>
                                </View>
                                <Pressable
                                    onPress={async () => { try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {} if (isScanning) { cancelScan(); } onClose(); }}
                                    style={({ pressed }) => ({ padding: 10, backgroundColor: pressed ? '#2E3238' : '#23262B', borderRadius: 8 })}
                                >
                                    <Text style={{ color: 'white' }}>Close</Text>
                                </Pressable>
                            </View>
                            {/* Success overlay rendered above camera */}
                            {successOverlay?.open ? (
                                <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
                                    <View style={{ backgroundColor: '#292D33', width: '85%', maxWidth: 360, padding: 20, borderRadius: 12, alignItems: 'center' }}>
                                        <Text style={{ color: '#FFFFFF', fontSize: 18, fontWeight: '700', marginBottom: 8 }}>Charger Added</Text>
                                        <Text style={{ color: '#8D8E90', fontSize: 14, textAlign: 'center', marginBottom: 16 }}>
                                            Your {successOverlay.manufacturer} charger, SN# {successOverlay.serial}, has been successfully added
                                        </Text>
                                        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Pressable onPress={() => setSuccessOverlay(null)} style={{ backgroundColor: '#51B868', paddingVertical: 10, paddingHorizontal: 16, borderRadius: 24, margin: 4 }}>
                                                <Text style={{ color: '#FFFFFF', fontWeight: '700' }}>Add Another</Text>
                                            </Pressable>
                                            <Pressable onPress={() => { setSuccessOverlay(null); onClose(); }} style={{ backgroundColor: '#3A3F45', paddingVertical: 10, paddingHorizontal: 16, borderRadius: 24, margin: 4 }}>
                                                <Text style={{ color: '#FFFFFF', fontWeight: '700' }}>Done</Text>
                                            </Pressable>
                                            <Pressable onPress={async () => {
                                                try {
                                                    if (successOverlay?.chargerId) {
                                                        await removePartnerCharger({ chargerId: successOverlay.chargerId });
                                                    }
                                                } catch {}
                                                setItems(prev => prev.filter(i => !(i.status === 'valid' && i.serial === successOverlay?.serial)));
                                                processed.current.delete((successOverlay?.serial || '').toUpperCase());
                                                setSuccessOverlay(null);
                                            }} style={{ backgroundColor: '#8E2C2C', paddingVertical: 10, paddingHorizontal: 16, borderRadius: 24, margin: 4 }}>
                                                <Text style={{ color: '#FFFFFF', fontWeight: '700' }}>Undo</Text>
                                            </Pressable>
                                        </View>
                                    </View>
                                </View>
                            ) : null}
                        </View>
                    ) : (
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                            <MaterialIcons name="no-photography" size={120} color="#FFFFFF" style={{ marginBottom: 16 }} />
                            <Text style={{ color: 'white', marginBottom: 12 }}>Camera permission is required to scan serials.</Text>
                            <Pressable onPress={onClose} style={{ padding: 12, backgroundColor: '#23262B', borderRadius: 8 }}>
                                <Text style={{ color: 'white' }}>Close</Text>
                            </Pressable>
                        </View>
                    )}
                </View>

                

                {/* List */}
                {!singleMode && (
                    <View style={{ flex: 1 }}>
                        <FlatList
                            data={items}
                            keyExtractor={(i) => i.id}
                            renderItem={renderItem}
                            keyboardShouldPersistTaps="handled"
                            contentContainerStyle={{ paddingBottom: 24 }}
                        />
                    </View>
                )}
            </View>
            </TouchableWithoutFeedback>
        </Modal>
    ) : null;
};

export default SerialScanModal;


